// Dark web breach monitoring + search presence fixtures.
//
// Covers stores/person/dark-web-scan.ts (breach list + scan acceptance) and
// services/search-presence-service.ts (results list, statistics, update,
// scan, scan status, mark-as-exposure) for the single demo persona.

import type { DemoRoute } from '../types';
import { refusal } from '../envelope';
import { isDemoPlanLocked } from '../mode';
import { DEMO_PERSON_ID, DEMO_PERSONA, demoDaysAgo } from '../persona';
import type { PersonBreach, BreachesResponse } from '~/types/person/breaches';
import type {
    SearchResult,
    SearchResultsResponse,
    SearchResultStatus,
    SearchResultUpdatePayload,
    MarkAsExposurePayload,
    ScanStatus,
} from '~/types/person/search-presence';

// ---------------------------------------------------------------------------
// Dark web breaches
// ---------------------------------------------------------------------------

const WORK_EMAIL = DEMO_PERSONA.email;
const WORK_EMAIL_ALT = 'mscott@dundermifflin.com';
const PERSONAL_EMAIL = 'michaelscarn@gmail.com';

type Severity = PersonBreach['severity'];

/** [id, domain, companyName, email, breachDaysAgo, addedDaysAgo, dataExposed, severity, isVerified, closedDaysAgo] */
type BreachSeed = [number, string, string, string, number, number, string[], Severity, boolean, number | null];

const BREACH_SEEDS: BreachSeed[] = [
    [1, 'linkedin.com', 'LinkedIn', WORK_EMAIL, 5188, 730, ['email', 'passwords', 'personal'], 'high', true, 200],
    [2, 'adobe.com', 'Adobe', WORK_EMAIL, 4702, 700, ['email', 'passwords', 'personal', 'banking'], 'critical', true, 180],
    [3, 'dropbox.com', 'Dropbox', WORK_EMAIL_ALT, 5162, 650, ['email', 'passwords'], 'medium', true, 150],
    [4, 'tumblr.com', 'Tumblr', PERSONAL_EMAIL, 4923, 600, ['email', 'passwords', 'usernames'], 'medium', true, 130],
    [5, 'myfitnesspal.com', 'MyFitnessPal', PERSONAL_EMAIL, 3094, 400, ['email', 'passwords', 'usernames'], 'high', true, 90],
    [6, 'myheritage.com', 'MyHeritage', PERSONAL_EMAIL, 3219, 380, ['email', 'passwords'], 'low', false, 60],
    [7, 'chegg.com', 'Chegg', PERSONAL_EMAIL, 3033, 300, ['email', 'passwords', 'address', 'personal'], 'high', true, null],
    [8, 'canva.com', 'Canva', WORK_EMAIL_ALT, 2637, 250, ['email', 'usernames', 'passwords', 'personal'], 'medium', true, null],
    [9, 'zynga.com', 'Zynga', PERSONAL_EMAIL, 2544, 200, ['email', 'usernames', 'passwords', 'phone_number'], 'high', true, null],
    [10, 'wattpad.com', 'Wattpad', PERSONAL_EMAIL, 2241, 150, ['email', 'usernames', 'passwords', 'personal'], 'medium', true, null],
    [11, 'ticketmaster.com', 'Ticketmaster', WORK_EMAIL, 841, 60, ['email', 'personal', 'phone_number', 'banking'], 'critical', true, null],
];

const BREACHES: PersonBreach[] = BREACH_SEEDS.map(
    ([id, domain, companyName, email, breachDaysAgo, addedDaysAgo, dataExposed, severity, isVerified, closedDaysAgo]) => ({
        id,
        domain,
        email,
        breach_date: demoDaysAgo(breachDaysAgo),
        added_date: demoDaysAgo(addedDaysAgo),
        data_exposed: dataExposed,
        breach_logo: null,
        closed_at: closedDaysAgo === null ? null : demoDaysAgo(closedDaysAgo),
        severity,
        is_verified: isVerified,
        company: { name: companyName, logo: null },
    })
);

const BREACHES_META = {
    total: BREACHES.length,
    open: BREACHES.filter((b) => !b.closed_at).length,
    closed: BREACHES.filter((b) => !!b.closed_at).length,
    // A check has finished for this persona, so the list is an answer and the page may read a
    // verdict off it. A demo that never reported one would sit on "No result to show yet".
    last_checked_at: demoDaysAgo(0.02),
};

/**
 * The answers `POST people/{id}/breaches/scan` and `GET people/{id}/breaches` give on each side
 * of the plan gap. `?demo=locked` runs the persona without breach monitoring, so both routes
 * below answer the refusal and the locked states are walkable; the licensed demo gets the
 * acceptance and the list. The store's spec drives the same two constants, so one place states
 * what these endpoints answer and a shape that drifts fails a test rather than a member's page.
 */
export const BREACH_SCAN_QUEUED = { data: { status: 'queued' } };

export const BREACH_ENTITLEMENT_REFUSAL = refusal(`people/${DEMO_PERSON_ID}/breaches`, 403, 'Breach monitoring is not included in your plan.', {
    reason: 'entitlement_required',
    required_feature: 'breach_monitoring',
});

// ---------------------------------------------------------------------------
// Search presence
// ---------------------------------------------------------------------------

type Engine = 'google' | 'bing' | 'yahoo' | 'yandex';
type ExposureKind = 'none' | 'pending' | 'closed';

/** [domain, path, title, snippet, status, engines, firstSeenDaysAgo, lastSeenDaysAgo, exposureKind, exposureDaysAgo] */
type ResultSeed = [string, string, string, string, SearchResultStatus, Engine[], number, number, ExposureKind, number];

const RESULT_SEEDS: ResultSeed[] = [
    // Active — no removal requested yet
    ['whitepages.com', '/name/Michael-Scott/Scranton-PA', 'Michael Scott - Scranton, PA | Whitepages', 'Michael Scott, age 61, currently living in Scranton, PA. Possible relatives, phone numbers, and address history for this Dunder Mifflin employee.', 'active', ['google', 'bing'], 500, 5, 'none', 0],
    ['spokeo.com', '/Michael-Scott/Pennsylvania/Scranton', 'Michael Scott in Scranton, PA - Spokeo', "View Michael Scott's profile: age, phone, address, email, and background report. Employer: Dunder Mifflin Paper Company.", 'active', ['google'], 420, 10, 'none', 0],
    ['peoplefinders.com', '/people/Michael-Scott_Scranton-PA', 'Michael Scott, Scranton PA - PeopleFinders', 'Find contact info, relatives, and address history for Michael Scott of Scranton, Pennsylvania.', 'active', ['google'], 300, 20, 'none', 0],
    ['truthfinder.com', '/people/michael-scott-pa', 'Michael Scott - TruthFinder People Search', 'Uncover public records for Michael Scott in Scranton, Pennsylvania including phone, address, and social profiles.', 'active', ['google'], 240, 30, 'none', 0],
    ['intelius.com', '/people/Michael-Scott/Scranton-PA', 'Michael Scott Scranton PA | Intelius', "Michael Scott's public record profile — Scranton, PA. Includes email addresses and past employers.", 'active', ['google', 'yahoo'], 200, 6, 'none', 0],
    ['radaris.com', '/p/Michael/Scott/Scranton-PA', 'Michael Scott, Scranton, PA - Radaris', 'Public records, contact info and background details for Michael Scott of Scranton, Pennsylvania.', 'active', ['google'], 150, 15, 'none', 0],
    ['fastpeoplesearch.com', '/name/Michael-Scott_Scranton-PA', 'Michael Scott - Scranton PA - FastPeopleSearch', 'Free public records for Michael Scott, Scranton, Pennsylvania. Phone numbers and address history.', 'active', ['google', 'bing', 'yahoo', 'yandex'], 90, 2, 'none', 0],
    ['usphonebook.com', '/michael-scott/scranton-pa', 'Michael Scott Phone Number - Scranton, PA | USPhoneBook', "Look up Michael Scott's phone number and address in Scranton, Pennsylvania.", 'active', ['google'], 60, 1, 'none', 0],
    // Active — removal in progress
    ['beenverified.com', '/people/michael-scott/pa/scranton', 'Michael Scott - Public Records - BeenVerified', 'Search public records for Michael Scott of Scranton, Pennsylvania. Includes work history at Dunder Mifflin.', 'active', ['google', 'bing', 'yahoo'], 380, 12, 'pending', 6],
    ['instantcheckmate.com', '/people/michael-scott/pennsylvania', 'Michael Scott Background Report | Instant Checkmate', 'Comprehensive background check for Michael Scott, Scranton, PA. Employment records show Dunder Mifflin.', 'active', ['google', 'bing'], 260, 4, 'pending', 3],
    ['mylife.com', '/Michael-Scott_bqcz', 'Michael Scott - Scranton, PA - MyLife', "Michael Scott's reputation profile. Lives in Scranton, PA. Works at Dunder Mifflin Paper Company as Regional Manager.", 'active', ['google', 'bing'], 180, 3, 'pending', 2],
    ['nuwber.com', '/people/michael-scott-scranton-pa', 'Michael Scott - Nuwber', 'Michael Scott public profile: age, address, and relatives in Scranton, PA.', 'active', ['google', 'bing'], 45, 1, 'pending', 1],
    // Removed
    ['clustrmaps.com', '/person/Michael-Scott/Scranton-PA', 'Michael Scott - Scranton, PA | ClustrMaps', 'This archived listing for Michael Scott of Scranton, PA has been successfully removed.', 'removed', ['google'], 500, 60, 'closed', 55],
    ['peekyou.com', '/michael+scott', 'Michael Scott - Scranton, PA | PeekYou', 'This profile for Michael Scott of Scranton, PA has been successfully removed.', 'removed', ['google', 'bing'], 460, 70, 'closed', 65],
    ['checkpeople.com', '/michael-scott/pa', 'Michael Scott - Scranton, PA | CheckPeople', "This listing for Michael Scott's public records in Scranton, PA has been successfully removed.", 'removed', ['google'], 350, 90, 'closed', 85],
    ['cyberbackgroundchecks.com', '/people/Michael_Scott/Scranton-PA', 'Michael Scott - Scranton, PA | CyberBackgroundChecks', 'This background report for Michael Scott of Scranton, PA has been successfully removed.', 'removed', ['google', 'yahoo'], 300, 100, 'closed', 95],
    ['publicrecords360.com', '/Michael-Scott-Scranton-PA', 'Michael Scott - Scranton, PA | PublicRecords360', 'This public record for Michael Scott of Scranton, PA has been successfully removed.', 'removed', ['google'], 250, 120, 'closed', 115],
    ['addresses.com', '/michael-scott/scranton-pa', 'Michael Scott - Scranton, PA | Addresses.com', 'This address listing for Michael Scott of Scranton, PA has been successfully removed.', 'removed', ['google', 'bing'], 220, 150, 'closed', 140],
    ['411.com', '/name/Michael-Scott/PA', 'Michael Scott - Scranton, PA | 411.com', 'This directory listing for Michael Scott of Scranton, PA has been successfully removed.', 'removed', ['google'], 180, 170, 'closed', 165],
    ['advancedbackgroundchecks.com', '/Michael-Scott/PA', 'Michael Scott - Scranton, PA | AdvancedBackgroundChecks', 'This background report for Michael Scott of Scranton, PA has been successfully removed.', 'removed', ['google', 'bing'], 150, 30, 'closed', 25],
    // Ignored / preserved — legitimate profiles Michael wants kept
    ['linkedin.com', '/in/michael-scott-dundermifflin', 'Michael Scott - Regional Manager - Dunder Mifflin | LinkedIn', "Michael Scott's professional profile. Regional Manager at Dunder Mifflin Paper Company, Scranton, PA.", 'ignored', ['google', 'bing'], 900, 5, 'none', 0],
    ['facebook.com', '/michael.scott.scranton', 'Michael Scott | Facebook', 'Michael Scott is on Facebook. Join Facebook to connect with Michael Scott and others in Scranton, PA.', 'ignored', ['google'], 800, 10, 'none', 0],
    ['twitter.com', '/michaelscarn', 'Michael Scarn (@michaelscarn) / Twitter', 'The latest posts from Michael Scarn. Scranton, PA.', 'ignored', ['google', 'bing'], 700, 20, 'none', 0],
    ['reddit.com', '/user/mscott_dm', 'u/mscott_dm - Reddit', 'Overview for Reddit user mscott_dm, Scranton, PA.', 'ignored', ['google'], 600, 45, 'none', 0],
    ['instagram.com', '/michaelscottdm', 'Michael Scott (@michaelscottdm) • Instagram photos and videos', "Regional Manager. World's Best Boss. Scranton, PA.", 'ignored', ['google'], 500, 8, 'none', 0],
    ['dundermifflin.com', '/team/michael-scott', 'Michael Scott - Regional Manager | Dunder Mifflin', 'Official company bio for Michael Scott, Regional Manager, Scranton, PA branch.', 'ignored', ['google', 'bing', 'yahoo'], 950, 2, 'none', 0],
];

/** Deterministic, path-safe stand-in for a server-computed URL hash. */
function urlHashFor(canonicalUrl: string): string {
    let hash = 0;
    for (let i = 0; i < canonicalUrl.length; i++) {
        hash = (hash * 31 + canonicalUrl.charCodeAt(i)) >>> 0;
    }
    return hash.toString(16).padStart(8, '0');
}

const SEARCH_RESULTS: SearchResult[] = RESULT_SEEDS.map(
    ([domain, path, title, snippet, status, engines, firstSeenDaysAgo, lastSeenDaysAgo, exposureKind, exposureDaysAgo]) => {
        const canonical_url = `${domain}${path}`;
        const url_hash = urlHashFor(canonical_url);
        const hasExposure = exposureKind !== 'none';
        const closed = exposureKind === 'closed';

        return {
            user_id: DEMO_PERSON_ID,
            url_hash,
            status,
            first_seen_at: demoDaysAgo(firstSeenDaysAgo),
            last_seen_at: demoDaysAgo(lastSeenDaysAgo),
            exposure_id: hasExposure ? `exp_${url_hash}` : null,
            exposure_in_progress_at: hasExposure ? demoDaysAgo(closed ? exposureDaysAgo + 8 : exposureDaysAgo) : null,
            exposure_closed_at: closed ? demoDaysAgo(exposureDaysAgo) : null,
            canonical_url,
            full_url: `https://${canonical_url}`,
            title,
            snippet,
            seen_google: engines.includes('google'),
            seen_bing: engines.includes('bing'),
            seen_yahoo: engines.includes('yahoo'),
            seen_yandex: engines.includes('yandex'),
        };
    }
);

function findResultIndex(urlHash: string): number {
    return SEARCH_RESULTS.findIndex((r) => r.url_hash === urlHash);
}

// Shape matches search-presence-service.ts#getStatistics's inline return type —
// the exported SearchPresenceStats type in types/person/search-presence.ts is unused by that call.
// Derived from SEARCH_RESULTS on every call so a status change or exposure is reflected
// immediately, instead of freezing the counts the demo persona started with.
function computeStatistics() {
    return {
        auto_escalations: 2,
        removals_in_progress: SEARCH_RESULTS.filter((r) => r.status === 'active' && r.exposure_id && !r.exposure_closed_at).length,
        successful_removals: SEARCH_RESULTS.filter((r) => r.status === 'removed').length,
        ignored_results: SEARCH_RESULTS.filter((r) => r.status === 'ignored').length,
        actively_exposed_results: SEARCH_RESULTS.filter((r) => r.status === 'active').length,
        by_engine: {
            google: SEARCH_RESULTS.filter((r) => r.seen_google).length,
            bing: SEARCH_RESULTS.filter((r) => r.seen_bing).length,
            yahoo: SEARCH_RESULTS.filter((r) => r.seen_yahoo).length,
            yandex: SEARCH_RESULTS.filter((r) => r.seen_yandex).length,
        },
    };
}

const STATISTICS = computeStatistics();

// Always completed — a demo account never sits behind a scanning spinner.
const SCAN_STATUS: ScanStatus = {
    status: 'completed',
    started_at: demoDaysAgo(2.02),
    completed_at: demoDaysAgo(2),
    stats: {
        total_results: SEARCH_RESULTS.length,
        new_results: 4,
        updated_results: 15,
        engines_searched: 4,
        by_engine: {
            google: { results_found: STATISTICS.by_engine.google, new: 3, updated: 12 },
            bing: { results_found: STATISTICS.by_engine.bing, new: 1, updated: 5 },
            yahoo: { results_found: STATISTICS.by_engine.yahoo, new: 0, updated: 2 },
            yandex: { results_found: STATISTICS.by_engine.yandex, new: 0, updated: 1 },
        },
    },
    error: null,
};

// ---------------------------------------------------------------------------
// Routes — specific patterns before the generic ones they'd otherwise shadow.
// ---------------------------------------------------------------------------

export const discoveryRoutes: DemoRoute[] = [
    {
        method: 'POST',
        pattern: new RegExp(`^people/${DEMO_PERSON_ID}/breaches/scan$`),
        status: () => (isDemoPlanLocked() ? 403 : 202),
        respond: () => (isDemoPlanLocked() ? BREACH_ENTITLEMENT_REFUSAL : BREACH_SCAN_QUEUED),
    },
    {
        pattern: new RegExp(`^people/${DEMO_PERSON_ID}/breaches$`),
        status: () => (isDemoPlanLocked() ? 403 : 200),
        respond: (): BreachesResponse | typeof BREACH_ENTITLEMENT_REFUSAL =>
            isDemoPlanLocked() ? BREACH_ENTITLEMENT_REFUSAL : { data: BREACHES, meta: BREACHES_META },
    },

    {
        pattern: new RegExp(`^people/${DEMO_PERSON_ID}/search-results/statistics$`),
        respond: () => computeStatistics(),
    },
    {
        pattern: new RegExp(`^people/${DEMO_PERSON_ID}/search-results/scan/status$`),
        respond: () => ({ status: SCAN_STATUS }),
    },
    {
        method: 'POST',
        pattern: new RegExp(`^people/${DEMO_PERSON_ID}/search-results/scan$`),
        respond: () => ({ data: { message: 'Search presence scan complete.', status: SCAN_STATUS } }),
    },
    {
        method: 'POST',
        pattern: new RegExp(`^people/${DEMO_PERSON_ID}/search-results/mark-as-exposure$`),
        respond: (req) => {
            const { url_hash } = req.body as MarkAsExposurePayload;
            const exposure_id = `exp_${url_hash}`;
            const index = findResultIndex(url_hash);

            if (index !== -1) {
                SEARCH_RESULTS[index] = { ...SEARCH_RESULTS[index], exposure_id };
            }

            return {
                data: {
                    message: 'Exposure created successfully.',
                    exposure_id,
                },
            };
        },
    },
    {
        pattern: new RegExp(`^people/${DEMO_PERSON_ID}/search-results$`),
        respond: (req): SearchResultsResponse => {
            const status = req.query.get('status') as SearchResultStatus | null;
            const matches = (r: SearchResult) => !status || r.status === status;

            // The cursor indexes the source array, not the filtered view. An offset into the
            // filtered list goes stale the moment a result changes status: the list shrinks
            // under a cursor the caller is still holding, and the item that slid into its
            // place is never served.
            const pageSize = 10;
            const start = Number(req.query.get('after') ?? 0);
            const page: SearchResult[] = [];

            let cursor = start;
            for (; cursor < SEARCH_RESULTS.length && page.length < pageSize; cursor += 1) {
                const result = SEARCH_RESULTS[cursor];
                if (matches(result)) page.push(result);
            }

            const more = SEARCH_RESULTS.slice(cursor).some(matches);

            return {
                data: page,
                next_page: more ? String(cursor) : null,
            };
        },
    },
    {
        method: 'PUT',
        pattern: new RegExp(`^people/${DEMO_PERSON_ID}/search-results/([^/]+)$`),
        respond: (req) => {
            const urlHash = req.match[1];
            const { status } = req.body as SearchResultUpdatePayload;
            const index = findResultIndex(urlHash);

            if (index === -1) {
                return { data: { ...SEARCH_RESULTS[0], url_hash: urlHash, status } };
            }

            SEARCH_RESULTS[index] = { ...SEARCH_RESULTS[index], status };
            return { data: SEARCH_RESULTS[index] };
        },
    },
];
